<!-- Aspect ratio tools, cropping tools and zoom-in/out will be hidden when cropping tool is used to select option
     size for Click-and-Drop crop from base image
     Figma: https://www.figma.com/file/IDKJxLEYVwkKgKXVvgMrX8/Click-%26-Drop-Enhancements?node-id=1062%3A5562&t=KJznbMRQKCikbrPl-4
-->
<template>
  <div class="cropping-canvas-toolbar" :class="cssClass">
    <div v-if="canChangeRatio" class="aspect-ratio-tools">
      <!-- TODO: Update logic for selected class -->
      <button
        type="button"
        class="btn-ratio-original"
        :class="{
          selected: isSelectedAspectRatio('original'),
        }"
        @click.prevent="handleAspectRatio('original')"
      >
        <svg-icon name="AspectRatioOriginal24" kind="action" />
        <span> {{ t("cropping_aspect_ratio_original") }} </span>
      </button>
      <button
        type="button"
        class="btn-ratio-freeform"
        :class="{
          selected: isSelectedAspectRatio('0'),
        }"
        @click.prevent="handleAspectRatio('0')"
      >
        <svg-icon name="AspectRatioFreeform24" kind="action" />
        <span> {{ t("cropping_aspect_ratio_freeform") }} </span>
      </button>
      <button
        type="button"
        class="btn-ratio-16-to-9"
        :class="{
          selected: isSelectedAspectRatio('16/9'),
        }"
        @click.prevent="handleAspectRatio('16/9')"
      >
        <svg-icon name="AspectRatioSixteenToNine24" kind="action" />
        <span> 16:9 </span>
      </button>
      <button
        type="button"
        class="btn-ratio-4-to-3"
        :class="{
          selected: isSelectedAspectRatio('4/3'),
        }"
        @click.prevent="handleAspectRatio('4/3')"
      >
        <svg-icon name="AspectRatioFourToThree24" kind="action" />
        <span> 4:3 </span>
      </button>
      <button
        type="button"
        class="btn-ratio-square"
        :class="{
          selected: isSelectedAspectRatio('1'),
        }"
        @click.prevent="handleAspectRatio('1')"
      >
        <svg-icon name="AspectRatioSquare24" kind="action" />
        <span> {{ t("cropping_aspect_ratio_square") }} </span>
      </button>
      <button
        type="button"
        class="btn-ratio-9-to-16"
        :class="{
          selected: isSelectedAspectRatio('9/16'),
        }"
        @click.prevent="handleAspectRatio('9/16')"
      >
        <svg-icon name="AspectRatioNineToSixteen24" kind="action" />
        <span> 9:16 </span>
      </button>
      <button
        type="button"
        class="btn-ratio-3-to-4"
        :class="{
          selected: isSelectedAspectRatio('3/4'),
        }"
        @click.prevent="handleAspectRatio('3/4')"
      >
        <svg-icon name="AspectRatioThreeToFour24" kind="action" />
        <span> 3:4 </span>
      </button>
    </div>

    <div v-if="hasCroppingTool" class="cropping-tools">
      <button v-if="canRotate" type="button" class="btn-rotate" @click.prevent="handleRotate">
        <svg-icon name="Rotate24" kind="action" />
        <span> {{ t("cropping_action_button_rotate") }} </span>
      </button>
      <button v-if="canUndo" type="button" class="btn-undo" @click.prevent="handleUndo">
        <svg-icon name="Undo24" kind="action" />
        <span> {{ t("cropping_action_button_undo") }} </span>
      </button>
      <button v-if="canReset" type="button" class="btn-clear" @click.prevent="handleClear">
        <svg-icon name="Reset24" kind="action" />
        <span> {{ t("cropping_action_button_reset") }} </span>
      </button>
    </div>

    <div class="zoom-actions">
      <button v-if="canZoom" type="button" class="btn-zoom-in" @click="handleZoomIn">
        <svg-icon name="ZoomIn24" kind="action" />
        <span>{{ t("cropping_action_button_zoom_in") }} </span>
      </button>
      <button v-if="canZoom" type="button" class="btn-zoom-out" @click="handleZoomOut">
        <svg-icon name="ZoomOut24" kind="action" />
        <span> {{ t("cropping_action_button_zoom_out") }} </span>
      </button>
      <button
        :disabled="!canZoom"
        type="button"
        class="btn-zoom-default"
        @click="handleZoomDefault"
      >
        <span>{{ zoomSizeLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CROP_ACTION } from "@plugins/cropping/cropping.constant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Props
const props = defineProps({
  canChangeRatio: { type: Boolean, default: true },
  canRotate: { type: Boolean, default: true },
  canUndo: { type: Boolean, default: true },
  canReset: { type: Boolean, default: true },
  canZoom: { type: Boolean, default: true },
  zoomData: { type: Number, required: true },
  selectedAspectRatio: { type: String, default: "" },
});

const { t } = useI18n();
const emit = defineEmits(["cropAction"]);

// Computed
const cssClass = computed(() => ({
  "has-aspect-ratio-tools": props.canChangeRatio,
}));

const zoomSizeLabel = computed(() =>
  t("tinymce_plugin_drawing_modal_zoom_size_label", {
    zoomSize: Math.round(props.zoomData * 100),
  }),
);

const hasCroppingTool = computed(() => props.canRotate || props.canUndo || props.canReset);

// Methods
const handleAspectRatio = (ratio: string) => {
  if (!props.canChangeRatio) return;
  emit("cropAction", CROP_ACTION.ASPECT_RATIO, ratio);
};

const handleRotate = () => {
  if (!props.canRotate) return;
  emit("cropAction", CROP_ACTION.ROTATE);
};

const handleUndo = () => {
  if (!props.canUndo) return;
  emit("cropAction", CROP_ACTION.UNDO);
};

const handleClear = () => {
  if (!props.canReset) return;
  emit("cropAction", CROP_ACTION.CLEAR);
};

const handleZoomIn = () => {
  if (!props.canZoom) return;
  emit("cropAction", CROP_ACTION.ZOOM_IN);
};

const handleZoomOut = () => {
  if (!props.canZoom) return;
  emit("cropAction", CROP_ACTION.ZOOM_OUT);
};

const handleZoomDefault = () => {
  if (!props.canZoom) return;
  emit("cropAction", CROP_ACTION.ZOOM_DEFAULT);
};

const isSelectedAspectRatio = (ratio: string) => {
  return props.selectedAspectRatio === ratio;
};
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.cropping-canvas-toolbar {
  &.has-aspect-ratio-tools {
    @include media("tablet-min") {
      flex: 0 0 8rem;
      padding-right: 1rem;
      max-width: 8rem;

      .cropping-tools {
        left: 8rem;
      }

      ~ .canvas-container {
        max-width: calc(100% - 8rem);
      }
    }
  }

  .aspect-ratio-tools {
    display: flex;
    align-items: center;

    @include media("mobile") {
      justify-content: space-between;
      margin-bottom: $spacing-05;
    }

    @include media("tablet-min") {
      flex-flow: column;
      padding-top: $layout-05;

      button {
        &:not(:last-of-type) {
          margin-bottom: $spacing-05;
        }
      }
    }

    button {
      @include carbon--type-style("label-02", true);
      display: flex;
      flex-flow: column;
      align-items: center;
      color: $icon-02;

      &.selected {
        color: $interactive-01;

        .sls-icon {
          [fill] {
            fill: $interactive-01;
          }
        }
      }
    }
  }

  .cropping-tools,
  .zoom-actions {
    button {
      @include carbon--type-style("button-02");
      display: flex;
      align-items: center;
      color: $interactive-01;
    }
  }

  .cropping-tools {
    display: flex;
    padding: $spacing-03;
    background-color: $ui-06;
    border-radius: 10px;
    z-index: z-map("default");

    @include media("mobile") {
      margin-bottom: $spacing-05;
    }

    @include media("tablet-min") {
      position: absolute;
      top: 0;
      left: 0;
      // Width to ensure that the cropping-tools doesn't overlap with the zoom-actions under
      // Tablet devices with smaller resolution, min-supported width of 768px
      width: 300px;
    }

    button {
      display: flex;
      align-items: center;

      &:not(:last-of-type) {
        margin-right: $spacing-06;
      }

      &.btn-clear {
        padding-left: $spacing-06;
        border-left: $divider-01;
      }

      .sls-icon {
        margin-right: $spacing-03;

        [fill] {
          fill: $interactive-01;
        }
      }
    }
  }

  .zoom-actions {
    display: flex;
    align-items: center;
    top: 0;
    right: 0;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;

      &.btn-zoom-default {
        width: auto;
        margin-left: $spacing-05;
        text-transform: uppercase;
      }
    }

    @include media("mobile") {
      display: none;
    }
  }
}
</style>
